import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { faFile, faFileSignature, faHouseCircleXmark, faSave, faVacuum } from "@fortawesome/pro-regular-svg-icons";
import { toast } from "react-toastify";
import { openModal } from "../../store/modal/modalReducer";
import NotificationToast from "../Helpers/NotificationToast";
import { Formik, Form, Field } from "formik";
import SubmitButton from "../Forms/SubmitButton";
import CustomButton from "../Helpers/CustomButton";
import { getBooking } from "../../store/booking/bookingActions";
import SettingsSwitch from "../Forms/SettingsSwitch";
import QuickFormDate from "../Forms/QuickFormDate";

export default function GridMoveIn({ setDoc, db }) {
    const [loading, setLoading] = useState(true);
    const [reprintInLoading, setReprintInLoading] = useState(false);
    const [reprintOutLoading, setReprintOutLoading] = useState(false);
    const { current_staff } = useSelector((state) => state.auth);
    const { current_booking } = useSelector((state) => state.booking);
    const dispatch = useDispatch();
    const [hasInFiles, setHasInFiles] = useState(false);
    const [inFiles, setInFiles] = useState(false);
    const [hasOutFiles, setHasOutFiles] = useState(false);
    const [outFiles, setOutFiles] = useState(false);
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);
    const [moveFiles, setMoveFiles] = useState([]);
    const [dynamicInspections, setDynamicInspections] = useState([]);
    const [hasDynamicSystem, setHasDynamicSystem] = useState(false);

    const loadFiles = async () => {
        if (!current_booking?.id) return;

        // Load dynamic inspections
        axios({
            method: "get",
            url: `${process.env.REACT_APP_API_URL}booking/${current_booking.id}/inspections`,
        })
            .then((response) => {
                if (response.data.success) {
                    setHasDynamicSystem(response.data.has_dynamic_system);
                    setDynamicInspections(response.data.inspections || []);
                }
            })
            .catch((error) => {
                console.error("Failed to load dynamic inspections:", error);
            });

        // Load legacy PDF files
        axios({
            method: "get",
            url: `${process.env.REACT_APP_API_URL}booking/${current_booking.id}/files`,
        })
            .then((response) => {
                const allFiles = [...(response.data.files || []), ...(response.data.untagged_files || [])];

                const movePdfs = allFiles.filter((file) => {
                    // Handle file_type as string, object, or relationship
                    let fileType;
                    if (typeof file.file_type === "string") {
                        fileType = file.file_type;
                    } else if (file.file_type_name) {
                        // Check legacy file_type_name first (raw database value)
                        fileType = file.file_type_name;
                    } else if (typeof file.file_type === "object" && file.file_type?.name) {
                        fileType = file.file_type.name;
                    } else if (file.fileType?.name) {
                        fileType = file.fileType.name;
                    }

                    return file.extension === "pdf" && (fileType === "move in form" || fileType === "move out form" || fileType === "cleaning form");
                });

                setMoveFiles(movePdfs);
            })
            .catch((error) => {
                console.error("Failed to load move files:", error);
            });

        // Check for move in/out image attachments
        axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}check_move_files_existence`,
            data: { userId: current_booking?.user.id, booking_id: current_booking.id },
        })
            .then((response) => {
                if (response.data[0] === 1) {
                    setHasInFiles(true);
                }
                if (response.data[1] === 1) {
                    setHasOutFiles(true);
                }
            })
            .catch((error) => {
                console.error("Failed to check files:", error);
            });
    };

    useEffect(() => {
        loadFiles();
    }, [current_booking?.id]);

    async function downloadMoveFiles(type) {
        setIsLoadingFiles(true);
        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}check_move_files`,
                data: {
                    userId: current_booking?.user.id,
                    booking_id: current_booking.id,
                    type: type,
                },
            });

            if (type === "in" && response.data[0] === 1) {
                setInFiles(response.data[1]);
                return response.data[1];
            } else if (type === "out" && response.data[2] === 1) {
                setOutFiles(response.data[3]);
                return response.data[3];
            }
            return null;
        } catch (error) {
            console.error("Failed to download files:", error);
            toast(<NotificationToast title="Error" message="Failed to download files" type="error" />);
            return null;
        } finally {
            setIsLoadingFiles(false);
        }
    }

    async function regenInForm(id) {
        setReprintInLoading(true);
        axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}regen_in`,
            data: { id: id },
        })
            .then((response) => {
                if (response.data == "No inspection form found") {
                    toast(<NotificationToast title="Error" message="No inspection form found" type="error" />);
                } else {
                    toast(<NotificationToast title="Success" message="Inspection form regenerated" type="success" />);
                    loadFiles(); // Reload files to show the new PDF
                }
                setReprintInLoading(false);
            })
            .catch((error) => {
                const errorMessage = error.response?.data || "Failed to regenerate inspection form";
                toast(<NotificationToast title="Error" message={errorMessage} type="error" />);
                setReprintInLoading(false);
            });
    }

    async function regenOutForm(id) {
        setReprintOutLoading(true);
        axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}regen_out`,
            data: { id: id },
        })
            .then((response) => {
                if (response.data == "No inspection form found" || response.data?.message == "No inspection form found") {
                    toast(<NotificationToast title="Error" message="No inspection form found" type="error" />);
                } else if (response.data?.success === false) {
                    toast(<NotificationToast title="Error" message={response.data?.message || "Failed to regenerate"} type="error" />);
                } else {
                    toast(<NotificationToast title="Success" message="Inspection form regenerated" type="success" />);
                    loadFiles(); // Reload files to show the new PDF
                }
                setReprintOutLoading(false);
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.message || error.response?.data || "Failed to regenerate inspection form";
                toast(<NotificationToast title="Error" message={errorMessage} type="error" />);
                setReprintOutLoading(false);
            });
    }

    async function regenPreOutForm(id) {
        setReprintOutLoading(true);
        axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}regen_pre_out`,
            data: { id: id },
        })
            .then((response) => {
                if (response.data == "No inspection form found" || response.data?.message == "No inspection form found") {
                    toast(<NotificationToast title="Error" message="No inspection form found" type="error" />);
                } else if (response.data?.success === false) {
                    toast(<NotificationToast title="Error" message={response.data?.message || "Failed to regenerate"} type="error" />);
                } else {
                    toast(<NotificationToast title="Success" message="Inspection form regenerated" type="success" />);
                    loadFiles(); // Reload files to show the new PDF
                }
                setReprintOutLoading(false);
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.message || error.response?.data || "Failed to regenerate inspection form";
                toast(<NotificationToast title="Error" message={errorMessage} type="error" />);
                setReprintOutLoading(false);
            });
    }

    // Filter out legacy PDFs that are already shown in dynamic inspections
    // If there's a dynamic inspection of a certain type, exclude ALL PDFs of that type from legacy
    const dynamicTypes = dynamicInspections
        .map((inspection) => {
            if (inspection.type === "move_in") return "move in form";
            if (inspection.type === "move_out") return "move out form";
            if (inspection.type === "pre_move_out") return "pre move out form";
            return null;
        })
        .filter(Boolean);

    const legacyFiles = moveFiles.filter((file) => !dynamicTypes.includes(file.file_type));

    // Check if we should show section headers
    const hasDynamicInspections = dynamicInspections.length > 0;
    const hasLegacyFiles = legacyFiles.length > 0;
    const showBothSections = hasDynamicInspections && hasLegacyFiles;

    return (
        <>
            <div className="h-96 overflow-y-scroll scroll-smooth">
                {/* Dynamic Inspections Section - Only show if there are actual dynamic inspections */}
                {hasDynamicInspections && (
                    <>
                        {/* Only show header if we have both sections */}
                        {showBothSections && (
                            <div className="mb-4 p-2 bg-blue-50 border-l-4 border-blue-500">
                                <h3 className="font-bold text-blue-900">📋 Dynamic Inspections (New System)</h3>
                                <p className="text-xs text-blue-700">Inspections with item-specific images</p>
                            </div>
                        )}

                        {dynamicInspections.map((inspection) => (
                            <div 
                                key={inspection.id} 
                                className={`mb-3 border-2 rounded p-2 ${
                                    inspection.type === 'move_in' && inspection.dirty === 1
                                        ? 'border-red-500 bg-red-50'
                                        : inspection.type === 'move_in'
                                        ? 'border-green-500 bg-green-50'
                                        : 'border-blue-300 bg-white'
                                }`}
                            >
                                <div className="mb-2 flex justify-between items-center">
                                    <p className={`text-sm font-semibold ${
                                        inspection.type === 'move_in' && inspection.dirty === 1
                                            ? 'text-red-900'
                                            : inspection.type === 'move_in'
                                            ? 'text-green-900'
                                            : 'text-blue-900'
                                    }`}>
                                        📋 {inspection.type_label} - {inspection.created_at_human}
                                        {inspection.type === 'move_in' && inspection.dirty === 1 && (
                                            <span className="ml-2 text-xs bg-red-600 text-white px-2 py-1 rounded">⚠️ Needs Attention</span>
                                        )}
                                        {inspection.type === 'move_in' && inspection.dirty === 0 && (
                                            <span className="ml-2 text-xs bg-green-600 text-white px-2 py-1 rounded">✓ All Good</span>
                                        )}
                                    </p>
                                    <div className="flex gap-2">
                                        {/* Regenerate button */}
                                        <button
                                            className="text-xs px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded"
                                            onClick={async () => {
                                                if (window.confirm(`Regenerate PDF for this ${inspection.type_label}?`)) {
                                                    try {
                                                        const endpoint =
                                                            inspection.type === "move_in" ? "regen_in_dynamic" : inspection.type === "pre_move_out" ? "regen_pre_out_dynamic" : "regen_out_dynamic";

                                                        const response = await axios.post(`${process.env.REACT_APP_API_URL}${endpoint}`, { id: inspection.id });

                                                        if (response.data === "No inspection form found") {
                                                            toast.error("No inspection form found");
                                                        } else {
                                                            toast.success(`${inspection.type_label} PDF regenerated successfully`);
                                                            // Reload inspections to show new PDF
                                                            const inspectionsResponse = await axios.get(`${process.env.REACT_APP_API_URL}booking/${current_booking.id}/inspections`);
                                                            if (inspectionsResponse.data.success) {
                                                                setDynamicInspections(inspectionsResponse.data.inspections || []);
                                                            }
                                                        }
                                                    } catch (error) {
                                                        toast.error(error.response?.data?.message || "Failed to regenerate PDF");
                                                    }
                                                }
                                            }}
                                        >
                                            🔄 Regenerate
                                        </button>
                                        {/* Delete button - only for user ID 1 (Roger) */}
                                        {current_staff?.id === 1 && (
                                            <button
                                                className="text-xs px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded"
                                                onClick={async () => {
                                                    if (window.confirm(`Delete this ${inspection.type_label}? This will remove the form, PDFs, and all uploaded images.`)) {
                                                        try {
                                                            const response = await axios.delete(`${process.env.REACT_APP_API_URL}inspection/${inspection.type}/${inspection.id}`);
                                                            if (response.data.success) {
                                                                toast.success(`${inspection.type_label} deleted successfully`);
                                                                // Reload inspections
                                                                const inspectionsResponse = await axios.get(`${process.env.REACT_APP_API_URL}booking/${current_booking.id}/inspections`);
                                                                if (inspectionsResponse.data.success) {
                                                                    setDynamicInspections(inspectionsResponse.data.inspections || []);
                                                                }
                                                                // Reload booking to update status
                                                                dispatch(getBooking(current_booking.id));
                                                            }
                                                        } catch (error) {
                                                            toast.error(error.response?.data?.message || "Failed to delete inspection");
                                                        }
                                                    }
                                                }}
                                            >
                                                🗑️ Delete
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* PDF Files (all versions) */}
                                {inspection.pdf_files && inspection.pdf_files.length > 0 && (
                                    <div className="ml-4 space-y-1">
                                        {inspection.pdf_files.map((pdf) => (
                                            <div
                                                key={pdf.id}
                                                className="cursor-pointer hover:bg-blue-50 p-2 rounded border border-blue-200"
                                                onClick={() =>
                                                    dispatch(
                                                        openModal({
                                                            modal_type: "ModalViewFiles",
                                                            modal_props: {
                                                                files: [{ id: pdf.id }],
                                                            },
                                                        }),
                                                    )
                                                }
                                            >
                                                <p className={`text-xs font-medium ${pdf.dirty == 0 ? "text-green-700" : "text-red-700"}`}>📄 {pdf.name}</p>
                                                <p className="text-xs text-gray-500">{pdf.created_at_human}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Images Button - Always show */}
                                <div className="ml-4 mt-2">
                                    <button
                                        className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                                            inspection.image_count > 0 ? "bg-blue-100 hover:bg-blue-200 text-blue-800" : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                                        }`}
                                        onClick={() => {
                                            if (inspection.image_count > 0) {
                                                dispatch(
                                                    openModal({
                                                        modal_type: "ModalInspectionImages",
                                                        modal_props: {
                                                            inspection: inspection,
                                                            images: inspection.images,
                                                        },
                                                    }),
                                                );
                                            } else {
                                                alert("No images attached to this inspection");
                                            }
                                        }}
                                    >
                                        🖼️ {inspection.image_count > 0 ? `${inspection.image_count} image${inspection.image_count !== 1 ? "s" : ""} attached` : "No images attached"}
                                    </button>
                                </div>
                            </div>
                        ))}

                        {/* Move Out Attachments button for dynamic inspections */}
                        {hasOutFiles === true && (
                            <>
                                <CustomButton
                                    text={isLoadingFiles ? "Downloading Files..." : "Move Out Attachments"}
                                    styling="w-full mt-2"
                                    icon={faFile}
                                    onClick={async () => {
                                        const files = await downloadMoveFiles("out");
                                        if (files) {
                                            dispatch(
                                                openModal({
                                                    modal_type: "ModalMoveFiles",
                                                    modal_props: { files: files },
                                                }),
                                            );
                                        }
                                    }}
                                    loading={isLoadingFiles}
                                />
                            </>
                        )}

                        {/* Move In Attachments button for dynamic inspections */}
                        {hasInFiles === true && (
                            <>
                                <CustomButton
                                    text={isLoadingFiles ? "Downloading Files..." : "Move In Attachments"}
                                    styling="w-full mt-2"
                                    icon={faFile}
                                    onClick={async () => {
                                        const files = await downloadMoveFiles("in");
                                        if (files) {
                                            dispatch(
                                                openModal({
                                                    modal_type: "ModalMoveFiles",
                                                    modal_props: { files: files },
                                                }),
                                            );
                                        }
                                    }}
                                    loading={isLoadingFiles}
                                />
                            </>
                        )}

                        {/* Only show separator and legacy header if we have both sections */}
                        {showBothSections && (
                            <>
                                <hr className="my-4 border-gray-300" />
                                <div className="mb-2 p-2 bg-gray-50 border-l-4 border-gray-400">
                                    <h3 className="font-bold text-gray-700">📁 Legacy Inspections (Old System)</h3>
                                    <p className="text-xs text-gray-600">Traditional inspection forms</p>
                                </div>
                            </>
                        )}
                    </>
                )}

                {/* Legacy PDF Files */}
                {legacyFiles && legacyFiles.length > 0 ? (
                    <>
                        {legacyFiles.map((file) => (
                            <div
                                key={file.id}
                                className="border-2 w-full rounded px-1 py-1 my-2 cursor-pointer hover:-translate-y-0.5 hover:bg-dark_background hover:text-white"
                                onClick={() =>
                                    dispatch(
                                        openModal({
                                            modal_type: "ModalViewFiles",
                                            modal_props: {
                                                files: [
                                                    {
                                                        id: file.id,
                                                    },
                                                ],
                                            },
                                        }),
                                    )
                                }
                            >
                                <p
                                    className={
                                        process.env.REACT_APP_COLOUR === "QL" || process.env.REACT_APP_COLOUR === "UC"
                                            ? file.dirty == 0
                                                ? "text-center break-words whitespace-normal bg-green-600 text-white"
                                                : "text-center break-words whitespace-normal bg-red-600 text-white"
                                            : "text-center break-words whitespace-normal"
                                    }
                                >
                                    {(file.name || "").replace(/_/g, " ")}
                                </p>
                            </div>
                        ))}
                    </>
                ) : (
                    ""
                )}

                {/* Move In/Out Attachments buttons - show here if NOT already shown in dynamic section */}
                {!hasDynamicInspections && hasInFiles === true && (
                    <CustomButton
                        text={isLoadingFiles ? "Downloading Files..." : "Move In Attachments"}
                        styling="w-full mt-2"
                        icon={faFile}
                        onClick={async () => {
                            const files = await downloadMoveFiles("in");
                            if (files) {
                                dispatch(
                                    openModal({
                                        modal_type: "ModalMoveFiles",
                                        modal_props: { files: files },
                                    }),
                                );
                            }
                        }}
                        loading={isLoadingFiles}
                    />
                )}

                {!hasDynamicInspections && hasOutFiles === true && (
                    <CustomButton
                        text={isLoadingFiles ? "Downloading Files..." : "Move Out Attachments"}
                        styling="w-full mt-2"
                        icon={faFile}
                        onClick={async () => {
                            const files = await downloadMoveFiles("out");
                            if (files) {
                                dispatch(
                                    openModal({
                                        modal_type: "ModalMoveFiles",
                                        modal_props: { files: files },
                                    }),
                                );
                            }
                        }}
                        loading={isLoadingFiles}
                    />
                )}

                {/* Legacy-only buttons - show if there are legacy files OR if this is an old system booking (no dynamic inspections) */}
                {(hasLegacyFiles || !hasDynamicInspections) && (
                    <>
                        {process.env.REACT_APP_COLOUR === "QL" ? (
                            <>
                                <CustomButton
                                    text="Customise TBA Pricing"
                                    styling="w-full mt-2"
                                    icon={faFileSignature}
                                    onClick={() =>
                                        dispatch(
                                            openModal({
                                                modal_type: "ModalCustomTba",
                                            }),
                                        )
                                    }
                                />

                                <br />
                            </>
                        ) : (
                            ""
                        )}

                        <CustomButton text="Re-print In" styling="w-full mt-2" icon={faFileSignature} onClick={() => regenInForm(current_booking?.id)} loading={reprintInLoading} />
                        <CustomButton text="Re-print Pre-Out" styling="w-full mt-2" icon={faFileSignature} onClick={() => regenPreOutForm(current_booking?.id)} loading={reprintOutLoading} />
                        <CustomButton text="Re-print Out" styling="w-full mt-2" icon={faFileSignature} onClick={() => regenOutForm(current_booking?.id)} loading={reprintOutLoading} />
                    </>
                )}

                <hr className="mt-8 mb-4" />

                <Formik
                    initialValues={{
                        in_inspection: current_booking.inspection,
                        first_month_paid: current_booking.first_month_paid,
                        first_month_paid_at: current_booking.first_month_paid_at,
                        in_inspection_at: current_booking.inspection_date,
                        key_issued: current_booking.key_issued,
                        key_issued_at: current_booking.key_issued_date,
                        biometrics_done: current_booking.biometrics,
                        biometrics_done_at: current_booking.biometrics_date,
                        move_out: current_booking.move_out,
                        move_out_at: current_booking.move_out_at,
                        pre_move_out: current_booking.pre_move_out,
                        pre_move_out_at: current_booking.pre_move_out_at,
                        cleaned: current_booking.cleaned,
                        cleaned_at: current_booking.cleaned_at,
                    }}
                    enableReinitialize
                    onSubmit={async (values, { setSubmitting, setErrors, resetForm }) => {
                        values.booking_id = current_booking.id;
                        values.current_staff = current_staff;

                        await axios({
                            method: "post",
                            url: `${process.env.REACT_APP_API_URL}user_move_in`,
                            data: values,
                        })
                            .then((response) => {
                                if (response.data && typeof response.data === 'string' && response.data.startsWith('Error:')) {
                                    toast(<NotificationToast title="Error" message={response.data} type="error" />);
                                } else {
                                    dispatch(getBooking(current_booking.id));
                                    toast(<NotificationToast title="Success" message="Data has been updated sucessfully" />);
                                }
                            })
                            .catch((error) => {
                                const errorMessage = error.response?.data?.message || error.response?.data || "Something went wrong";
                                toast(<NotificationToast title="Error" message={errorMessage} type="error" />);
                            });

                        setSubmitting(false);
                    }}
                >
                    {({ values, touched, errors, handleSubmit, setFieldValue, setFieldTouched, isValid, isSubmitting, dirty }) => (
                        <Form onSubmit={handleSubmit} className="rounded-3xl">
                            {process.env.REACT_APP_COLOUR === "UC" || process.env.REACT_APP_COLOUR === "QL" ? (
                                <>
                                    <p className="text-xl">Move In</p>
                                    <div className="space-y-4 sm:grid sm:grid-cols-6 gap-2 mb-5">
                                        <Field name="in_inspection" as={SettingsSwitch} label="In-inspection" description=" " />
                                        <Field name="in_inspection_at" type="text" as={QuickFormDate} label="At" />

                                        {process.env.REACT_APP_COLOUR === "UC" ? (
                                            <>
                                                <Field name="first_month_paid" as={SettingsSwitch} label="First Month Paid" description=" " />
                                                <Field name="first_month_paid_at" type="text" as={QuickFormDate} label="At" />

                                                <Field name="key_issued" as={SettingsSwitch} label="Key issued" description=" " />
                                                <Field name="key_issued_at" type="text" as={QuickFormDate} label="At" />

                                                <Field name="biometrics_done" as={SettingsSwitch} label="Biometrics done" description=" " />
                                                <Field name="biometrics_done_at" type="text" as={QuickFormDate} label="At" />
                                            </>
                                        ) : (
                                            <></>
                                        )}
                                    </div>

                                    {/* {!current_staff.building_manager && process.env.REACT_APP_COLOUR === "UC" ? 
                                            <>
                                                <SubmitButton name="Save" disabled={!isValid || isSubmitting || !dirty} isSubmitting={isSubmitting} icon={faSave} text="Update" style={{ width: "100%" }}/>
                                                <br/><br/>
                                            </>
                                        : "" } */}
                                </>
                            ) : (
                                ""
                            )}

                            {process.env.REACT_APP_COLOUR === "QL" ? (
                                <>
                                    <br />
                                    <hr />
                                    <p className="text-xl">Pre Move Out</p>

                                    <div className="space-y-4 sm:grid sm:grid-cols-6 gap-2 mb-5">
                                        <Field name="pre_move_out" as={SettingsSwitch} label="Pre out inspection" description=" " />
                                        <Field name="pre_move_out_at" type="text" as={QuickFormDate} label="At" />
                                    </div>

                                    {process.env.REACT_APP_COLOUR === "UC" ? (
                                        <>
                                            {!current_staff.building_manager ? (
                                                <SubmitButton
                                                    name="Save"
                                                    disabled={!isValid || isSubmitting || !dirty}
                                                    isSubmitting={isSubmitting}
                                                    icon={faSave}
                                                    text="Update"
                                                    style={{ width: "100%" }}
                                                />
                                            ) : (
                                                ""
                                            )}
                                        </>
                                    ) : (
                                        ""
                                    )}

                                    <CustomButton
                                        text="Pre Move out Form"
                                        styling="w-full mt-2"
                                        icon={faHouseCircleXmark}
                                        onClick={() =>
                                            dispatch(
                                                openModal({
                                                    modal_type: process.env.REACT_APP_COLOUR === "QL" ? "ModalBMCustom" : "ModalBMCustomUC",
                                                    modal_props: {
                                                        pre_move_out: true,
                                                    },
                                                }),
                                            )
                                        }
                                    />
                                </>
                            ) : (
                                ""
                            )}

                            {process.env.REACT_APP_COLOUR === "QL" || process.env.REACT_APP_COLOUR === "UC" ? (
                                <>
                                    <br />
                                    <hr />
                                    <p className="text-xl">Move Out</p>

                                    <div className="space-y-4 sm:grid sm:grid-cols-6 gap-2 mb-5">
                                        <Field name="move_out" as={SettingsSwitch} label="Out-inspection" description=" " />
                                        <Field name="move_out_at" type="text" as={QuickFormDate} label="At" />
                                    </div>

                                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id="is_migration"
                                                checked={current_booking?.is_migration || false}
                                                onChange={async (e) => {
                                                    const isChecked = e.target.checked;
                                                    try {
                                                        await axios.patch(`${process.env.REACT_APP_API_URL}bookings/${current_booking.id}/admin`, {
                                                            is_migration: isChecked,
                                                        });
                                                        toast(<NotificationToast title="Updated" message={isChecked ? "Marked as migration" : "Migration flag removed"} />);
                                                        dispatch(getBooking(current_booking.id));
                                                    } catch (error) {
                                                        toast(<NotificationToast title="Error" message="Failed to update migration status" type="error" />);
                                                    }
                                                }}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor="is_migration" className="ml-2 block text-sm font-medium text-gray-700">
                                                Is Migration
                                                {current_booking?.is_migration && (
                                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                        🔄 MIGRATION
                                                    </span>
                                                )}
                                            </label>
                                        </div>
                                        <p className="text-xs text-gray-600 mt-1 ml-6">
                                            Check this if tenant is moving to another property. Parking bays and biometrics will be retained during move-out.
                                        </p>
                                    </div>

                                    {process.env.REACT_APP_COLOUR === "UC" ? (
                                        <>
                                            {!current_staff.building_manager ? (
                                                <SubmitButton
                                                    name="Save"
                                                    disabled={!isValid || isSubmitting || !dirty}
                                                    isSubmitting={isSubmitting}
                                                    icon={faSave}
                                                    text="Update"
                                                    style={{ width: "100%" }}
                                                />
                                            ) : (
                                                ""
                                            )}
                                        </>
                                    ) : (
                                        ""
                                    )}

                                    <CustomButton
                                        text="Move out Form"
                                        styling="w-full mt-2"
                                        icon={faHouseCircleXmark}
                                        onClick={() =>
                                            dispatch(
                                                openModal({
                                                    modal_type: process.env.REACT_APP_COLOUR === "QL" ? "ModalBMCustom" : "ModalBMCustomUC",
                                                }),
                                            )
                                        }
                                    />
                                </>
                            ) : (
                                ""
                            )}

                            {process.env.REACT_APP_COLOUR === "QL" ? (
                                <>
                                    <br />
                                    <hr />
                                    <p className="text-xl">Cleaning</p>
                                    <div className="space-y-4 sm:grid sm:grid-cols-6 gap-2 mb-5">
                                        <Field name="cleaned" as={SettingsSwitch} label="Cleaned" description=" " />
                                        <Field name="cleaned_at" type="text" as={QuickFormDate} label="At" />
                                    </div>

                                    <CustomButton
                                        text="Cleaning Form"
                                        styling="w-full mb-2"
                                        icon={faVacuum}
                                        onClick={() =>
                                            dispatch(
                                                openModal({
                                                    modal_type: "ModalCleaning",
                                                }),
                                            )
                                        }
                                    />

                                    {!current_staff.building_manager ? (
                                        <SubmitButton name="Save" disabled={!isValid || isSubmitting || !dirty} isSubmitting={isSubmitting} icon={faSave} text="Update" style={{ width: "100%" }} />
                                    ) : (
                                        ""
                                    )}
                                </>
                            ) : (
                                ""
                            )}
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    );
}
